import { FontFamily } from "../content-builder/element-schema";
import { FulfillmentGroupForPackingSlip } from "../fulfillments/fulfillment-group";
import type { Team } from "../team";
import { UtmParameters } from "../utm";
import { PackingSlip, PackingSlipPageLayout } from "./packing-slip-definition";
export namespace PaddingSize {
  export const SMALL = 8;
  export const MEDIUM = 16;
  export const LARGE = 32;
}

export enum PackingSlipFontWeight {
  NORMAL = "normal",
  BOLD = "bold",
}

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export enum Alignment {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
}

export enum AlignItems {
  LEFT = "Left",
  CENTER = "Center",
  RIGHT = "Right",
}

export const AlignItemsDisplayToCssValue: Record<AlignItems, string> = {
  [AlignItems.LEFT]: "flex-start",
  [AlignItems.CENTER]: "center",
  [AlignItems.RIGHT]: "flex-end",
};

export const FONT_WEIGHTS = [
  PackingSlipFontWeight.NORMAL,
  PackingSlipFontWeight.BOLD,
] as const;

export const FONT_FAMILIES = [
  FontFamily.ARIAL,
  FontFamily.COURIER_NEW,
  FontFamily.GEORGIA,
  FontFamily.LUCIDA_SANS_UNICODE,
  FontFamily.TAHOMA,
  FontFamily.TIMES_NEW_ROMAN,
  FontFamily.TREBUCHET_MS,
  FontFamily.VERDANA,
] as const;

export const RICH_TEXT_VARIABLES: (keyof FulfillmentGroupForPackingSlip)[] = [
  "recipientFirstName",
  "recipientFullName",
  "orderNumber",
  "secondaryOrderName",
  "orderDate",
  "printedDate",
  "orderTotal",
  "requestedService",
  "shippingService",
  "salesChannel",
  "totalQuantity",
  "fulfilledFromMultipleLocationsText",
  "orderTags",
  "customerId",
  "customerNumOrders",
  "combinedDiscountCodes",
  "customDate1",
  "customDate2",
  "customDate3",
  "customString1",
  "customString2",
  "customString3",
  "customNumber1",
  "customNumber2",
  "customNumber3",
  "customAttributeDate1",
  "customAttributeDate2",
  "customAttributeDate3",
  "customAttributeString1",
  "customAttributeString2",
  "customAttributeString3",
  "customAttributeNumber1",
  "customAttributeNumber2",
  "customAttributeNumber3",
  "warehouseName",
  "boxNumber",
  "totalBoxes",
  "boxLabel",
  "boxBarcodeValue",
];

export type RichTextVariable = (typeof RICH_TEXT_VARIABLES)[number];
export type RichTextVariables = { [K in RichTextVariable]?: string };

export enum OrderItemTableColumnType {
  BIN = "bin",
  ITEM_THUMBNAIL = "itemThumbnail",
  ITEM_NAME_AND_DESCRIPTION = "itemDescription",
  QUANTITY = "quantity",
  UNIT_PRICE = "unitPrice",
  PRICE = "price",
  SKU = "sku",
  UPC = "upc",
  VENDOR = "vendor",
  CUSTOM_FIELD = "customField",
}

export enum OrderItemSort {
  ORDER_NUMBER = "order-number",
  ORDER_SKU = OrderItemTableColumnType.SKU,
  ORDER_UPC = OrderItemTableColumnType.UPC,
  ORDER_BIN = OrderItemTableColumnType.BIN,
  ORDER_ITEM_NAME = OrderItemTableColumnType.ITEM_NAME_AND_DESCRIPTION,
  ORDER_QUANTITY = OrderItemTableColumnType.QUANTITY,
  CUSTOM = OrderItemTableColumnType.CUSTOM_FIELD,
}

export enum PackingSlipSortDirection {
  ASC = "Ascending",
  DESC = "Descending",
}

export enum QrCodeSize {
  EXTRA_SMALL = "Extra Small",
  SMALL = "Small",
  MEDIUM = "Medium",
  LARGE = "Large",
}

export const QrCodeSizeToPixels: Record<QrCodeSize, number> = {
  [QrCodeSize.EXTRA_SMALL]: 75,
  [QrCodeSize.SMALL]: 100,
  [QrCodeSize.MEDIUM]: 150,
  [QrCodeSize.LARGE]: 200,
};

export const OrderItemSortToColumnType: Record<
  OrderItemSort,
  keyof OrderItemTableColumnData
> = {
  [OrderItemSort.ORDER_NUMBER]:
    OrderItemTableColumnType.ITEM_NAME_AND_DESCRIPTION,
  [OrderItemSort.ORDER_SKU]: OrderItemTableColumnType.SKU,
  [OrderItemSort.ORDER_UPC]: OrderItemTableColumnType.UPC,
  [OrderItemSort.ORDER_BIN]: OrderItemTableColumnType.BIN,
  // For backwards compatibility this must exist, but we point it to itemName
  [OrderItemSort.ORDER_ITEM_NAME]: "itemName",
  [OrderItemSort.ORDER_QUANTITY]: OrderItemTableColumnType.QUANTITY,
  [OrderItemSort.CUSTOM]: OrderItemTableColumnType.CUSTOM_FIELD, // Handled specially
};

export interface OrderItemTableColumn {
  type: OrderItemTableColumnType;
  enabled: boolean;
  id?: string | null;
  title?: string | null;
  relativeFontSize?: number | null;
  fontWeight?: PackingSlipFontWeight | null;
  alignment?: Alignment | null;
}

export interface OrderItemTableColumnData {
  bin: string;
  itemThumbnail: string;
  itemName: string;
  itemProductTitle: string;
  itemDescription: string;
  quantity: number;
  unitPrice: string;
  price: string;
  sku: string;
  upc: string;
  vendor: string;
  customField: string;
  selectedOptions: { name: string; value: string }[];
  customAttributes: { key: string; value: string }[];
  metafields: { key: string; value: string }[];
}

export const orderItemTableColumnTypeToLabel: Record<
  OrderItemTableColumnType,
  string
> = {
  [OrderItemTableColumnType.BIN]: "Bin",
  [OrderItemTableColumnType.ITEM_THUMBNAIL]: "Image",
  [OrderItemTableColumnType.ITEM_NAME_AND_DESCRIPTION]: "Product name",
  [OrderItemTableColumnType.QUANTITY]: "Quantity",
  [OrderItemTableColumnType.UNIT_PRICE]: "Unit price",
  [OrderItemTableColumnType.PRICE]: "Total price",
  [OrderItemTableColumnType.SKU]: "SKU",
  [OrderItemTableColumnType.UPC]: "UPC",
  [OrderItemTableColumnType.CUSTOM_FIELD]: "Custom field",
  [OrderItemTableColumnType.VENDOR]: "Vendor",
};

export enum PackingSlipBlockType {
  HEADER_MARKER = "header-marker",
  IMAGE = "image",
  LOGO = "logo",
  TEXT = "text",
  SPACER = "spacer",
  LINE = "line",
  COLUMN = "column",
  BILLING_ADDRESS = "billing-address",
  COMPANY_ADDRESS = "company-address",
  CONTACT_INFORMATION = "contact-information",
  NOTE_FROM_CUSTOMER = "note-from-customer",
  NOTE_TO_CUSTOMER = "note-to-customer",
  ORDER_BARCODE = "order-barcode",
  ORDER_DATE = "order-date",
  ORDER_ITEMS = "order-items",
  PRINTED_DATE = "printed-date",
  ORDER_DETAILS = "order-details",
  ORDER_NUMBER = "order-number",
  PACKAGE_INFORMATION = "package-information",
  //OUT_OF_STOCK_ITEMS = "out-of-stock-items", need more definition
  PAYMENT_BREAKDOWN = "payment-breakdown",
  PAYMENT_METHOD = "payment-method",
  //PURCHASE_ORDER_NUMBER = "purchase-order-number", need more definition
  PACKAGE_WEIGHT = "package-weight",
  //PROMOTIONAL_MESSAGE = "promotional-message", can this not be a text block?
  SHIPPING_ADDRESS = "shipping-address",
  SHIPPING_METHOD = "shipping-method",
  REQUESTED_SHIPPING_METHOD = "requested-shipping-method",
  //SPECIAL_INSTRUCTIONS = "special-instructions", can this not be a text block?
  TRACKING_NUMBER = "tracking-number",
  QR_CODE = "qr-code",
}

export enum PackingSlipBlockCategory {
  GENERAL = "general",
  ORDER = "order",
  MERCHANT = "merchant",
  CUSTOMER = "customer",
}

export namespace Section {
  export interface BaseSection {
    blockId: string;
    duplicate?: () => Section;
    sectionPadding: Padding;
    sectionColor?: string | null;
    alignSelf?: AlignItems | null;
  }

  export interface FontSection extends BaseSection {
    fontFamily?: string | null;
    fontSize?: number | null;
    textColor?: string | null;
    fontWeight?: PackingSlipFontWeight | null;
  }

  export interface AddressSection extends FontSection {
    header?: string | null;
    showHeader?: boolean | null;
    showName?: boolean | null;
    boldName?: boolean | null;
    showCompany?: boolean | null;
    showAddress?: boolean | null;
    showPhone?: boolean | null;
    showEmail?: boolean | null;
  }

  export interface BillingAddress extends AddressSection {
    type: PackingSlipBlockType.BILLING_ADDRESS;
  }

  export interface HeaderMarker extends BaseSection {
    type: PackingSlipBlockType.HEADER_MARKER;
  }
  export interface Column extends BaseSection {
    type: PackingSlipBlockType.COLUMN;
    columns: SectionWithoutColumn[];
    columnCount: number;
    gap: number;
  }

  export interface CompanyAddress extends AddressSection {
    type: PackingSlipBlockType.COMPANY_ADDRESS;
  }

  export interface ContactInformation extends FontSection {
    type: PackingSlipBlockType.CONTACT_INFORMATION;
    showName: boolean;
    showCompany?: boolean | null;
    showAddress: boolean;
    showPhone: boolean;
    showEmail: boolean;
  }

  export interface NoteFromCustomer extends FontSection {
    type: PackingSlipBlockType.NOTE_FROM_CUSTOMER;
  }

  export interface NoteToCustomer extends FontSection {
    type: PackingSlipBlockType.NOTE_TO_CUSTOMER;
  }

  export interface OrderBarcode extends BaseSection {
    type: PackingSlipBlockType.ORDER_BARCODE;
    width?: number | null;
    height?: number | null;
    useOrderNumber?: boolean | null;
    useCustomerName?: boolean | null;
    useCustomerId?: boolean | null;
    // When true, encodes the per-box identifier (`<fgId>-<boxNumber>`) on
    // consolidation / per-parcel slips. Falls back to the FG-id barcode when
    // box context is missing — so a stray box-barcode block on a non-parcel
    // slip still scans instead of rendering an empty barcode.
    useBoxBarcode?: boolean | null;
    showAnnotation?: boolean | null;
    removeMarketplaceIdentifier?: boolean | null; // removes BC-, EBAY-, CS- etc. Will not work within redo
  }

  export interface OrderDate extends FontSection {
    type: PackingSlipBlockType.ORDER_DATE;
  }

  export interface PrintedDate extends FontSection {
    type: PackingSlipBlockType.PRINTED_DATE;
    showPrefix?: boolean | null;
  }

  export interface OrderItems extends FontSection {
    type: PackingSlipBlockType.ORDER_ITEMS;
    sort: OrderItemSort;
    sortDirection?: PackingSlipSortDirection | null;
    customSortField?: string | null; // When sort is CUSTOM, this specifies which custom field
    tableColumns: OrderItemTableColumn[];
    includeMetafields?: boolean | null;
    emphasizeQuantity?: boolean | null;
    rowHeight?: number | null;
    showDescription?: boolean | null;
    includeHeader?: boolean | null;
    tableBorderColor?: string | null;
    tableBorderWidth?: number | null;
    customAttributes?: string[] | null;
    showAllCustomAttributes?: boolean | null;
    splitItemsToSingleQuantity?: boolean | null;
    showOnlyProductTitle?: boolean | null;
  }

  export interface OrderDetails extends FontSection {
    type: PackingSlipBlockType.ORDER_DETAILS;
    showRecipientInformation: boolean;
    showOrderNumber: boolean;
    showOrderDate: boolean;
    showShippingService: boolean;
    stacked?: boolean | null;
  }

  export interface OrderNumber extends FontSection {
    type: PackingSlipBlockType.ORDER_NUMBER;
  }

  export interface PackageInformation extends FontSection {
    type: PackingSlipBlockType.PACKAGE_INFORMATION;
  }

  export interface PaymentBreakdown extends FontSection {
    type: PackingSlipBlockType.PAYMENT_BREAKDOWN;
  }

  export interface PaymentMethod extends FontSection {
    type: PackingSlipBlockType.PAYMENT_METHOD;
    showSubtotal: boolean;
    showPaymentMethod: boolean;
    showPaymentDate: boolean;
  }

  export interface PackageWeight extends FontSection {
    type: PackingSlipBlockType.PACKAGE_WEIGHT;
  }

  export interface ShippingAddress extends AddressSection {
    type: PackingSlipBlockType.SHIPPING_ADDRESS;
  }

  export interface RequestedShippingMethod extends FontSection {
    type: PackingSlipBlockType.REQUESTED_SHIPPING_METHOD;
  }

  export interface ShippingMethod extends FontSection {
    type: PackingSlipBlockType.SHIPPING_METHOD;
  }

  export interface TrackingNumber extends FontSection {
    type: PackingSlipBlockType.TRACKING_NUMBER;
  }

  export interface QrCode extends BaseSection {
    type: PackingSlipBlockType.QR_CODE;
    url: string;
    size: QrCodeSize;
    useReturnsPortalUrl: boolean;
  }

  export interface Text extends BaseSection {
    type: PackingSlipBlockType.TEXT;
    textColor: string;
    fontSize: number;
    fontFamily: string;
    linkColor: string;
    text: string;
  }

  export interface Line extends BaseSection {
    type: PackingSlipBlockType.LINE;
    color: string;
  }

  export interface Spacer extends BaseSection {
    type: PackingSlipBlockType.SPACER;
    height: number;
  }

  export interface Image extends BaseSection {
    type: PackingSlipBlockType.IMAGE;
    imageUrl: string;
    showCaption: boolean;
    maxHeight?: number | null;
    caption?: string | null;
    altText?: string | null;
    clickthroughUrl?: string | null;
    aspectRatio?: number | null;
    resolution?: number | null;
  }

  export interface Logo extends BaseSection {
    type: PackingSlipBlockType.LOGO;
    imageUrl?: string | null;
    maxHeight?: number | null;
  }
}

export type SectionWithoutColumn =
  | Section.Text
  | Section.Line
  | Section.Spacer
  | Section.Image
  | Section.Logo
  | Section.HeaderMarker
  | Section.BillingAddress
  | Section.CompanyAddress
  | Section.ContactInformation
  | Section.NoteFromCustomer
  | Section.NoteToCustomer
  | Section.OrderBarcode
  | Section.OrderDate
  | Section.PrintedDate
  | Section.OrderItems
  | Section.OrderDetails
  | Section.OrderNumber
  | Section.PackageInformation
  | Section.PaymentBreakdown
  | Section.PaymentMethod
  | Section.PackageWeight
  | Section.RequestedShippingMethod
  | Section.ShippingAddress
  | Section.ShippingMethod
  | Section.TrackingNumber
  | Section.QrCode;

export type Section = SectionWithoutColumn | Section.Column;

export interface SharedProps {
  team: Team;
  template: Omit<PackingSlip, "sections" | "team">;
  isBuilder: boolean;
  blockId?: string;
  utm: UtmParameters;
  onUpdate?: (updater: (section: Section) => Section) => void;
}

export class PackingSlipUtmParameters extends UtmParameters {
  constructor(campaign?: string, contact?: string) {
    super({ source: "redo", medium: "packing-slip", campaign, contact });
  }

  override getSource(): string {
    return super.getSource() ?? "";
  }

  override getMedium(): string {
    return super.getMedium() ?? "";
  }
}

export enum ScreenSize {
  DESKTOP = "desktop",
  MOBILE = "mobile",
}

export enum PageSize {
  FULL_SIZE = "full-size",
  HALF_SIZE = "half-size",
  LABEL = "label",
  CARD_5X7 = "card-5x7",
}

export enum PageOrientation {
  PORTRAIT = "portrait",
  LANDSCAPE = "landscape",
}

export function getPageFormatDimensions(pageLayout: PackingSlipPageLayout) {
  switch (pageLayout.size) {
    case PageSize.FULL_SIZE: {
      return pageLayout.orientation === PageOrientation.PORTRAIT
        ? { width: 8.5, height: 11 }
        : { width: 11, height: 8.5 };
    }
    case PageSize.HALF_SIZE: {
      return pageLayout.orientation === PageOrientation.PORTRAIT
        ? { width: 5.5, height: 8.5 }
        : { width: 8.5, height: 5.5 };
    }
    case PageSize.LABEL: {
      return pageLayout.orientation === PageOrientation.PORTRAIT
        ? { width: 4, height: 6 }
        : { width: 6, height: 4 };
    }
    case PageSize.CARD_5X7: {
      return pageLayout.orientation === PageOrientation.PORTRAIT
        ? { width: 5, height: 7 }
        : { width: 7, height: 5 };
    }
    // No default
  }
  throw new Error(`Invalid page size: ${pageLayout.size}`);
}
